import { apiFetch } from "../common/api";

const HOST = import.meta.env.VITE_API_HOST;

/**
 * List all campaigns.
 * @returns {Promise<Array>}
 */
export async function listCampaigns() {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns`);
  if (resp.status !== 200) throw await resp.text();
  const data = await resp.json();
  return data.items;
}

/**
 * Get a single campaign.
 * @param {number} id
 * @returns {Promise<object>}
 */
export async function getCampaign(id) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}`);
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Create a new campaign.
 * @param {object} data — { trigger_type, config, name? }
 * @returns {Promise<object>}
 */
export async function createCampaign(data) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Update a campaign.
 * @param {number} id
 * @param {object} data — { config, enabled?, name? }
 * @returns {Promise<object>}
 */
export async function updateCampaign(id, data) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Delete a campaign.
 * @param {number} id
 */
export async function deleteCampaign(id) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}`, {
    method: "DELETE",
  });
  if (resp.status !== 200) throw await resp.text();
}

/**
 * Manually run a campaign.
 *
 * Returns as soon as the run is accepted — the send itself happens in the
 * background, so the result carries no counts. Poll getCampaignRunState for
 * progress.
 * @param {number} id
 * @returns {Promise<{campaign_id: number, run_state: string, already_running: boolean}>}
 */
export async function runCampaign(id) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}/run`, {
    method: "POST",
  });
  if (resp.status !== 202 && resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Ask a campaign run that is already in flight to stop.
 *
 * Cooperative: the send loop checks the request before every recipient, so at
 * most the recipient currently being sent to still goes out. Also accepted for
 * a campaign that is not running, in which case a run started afterwards aborts
 * immediately.
 * @param {number} id
 * @returns {Promise<{campaign_id: number, stop_requested: boolean, run_state: string}>}
 */
export async function stopCampaign(id) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}/stop`, {
    method: "POST",
  });
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Live state of a campaign run.
 * @param {number} id
 * @returns {Promise<{run_state: string, run_candidates: number, run_sent: number, run_failed: number, stop_requested: boolean, run_error: string|null}>}
 */
export async function getCampaignRunState(id) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}/run-state`);
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Get the notification log for a campaign.
 * @param {number} id
 * @param {number} [offset]
 * @param {number} [limit]
 * @returns {Promise<{items: Array, total: number, hasNext: boolean, offset: number, limit: number}>}
 */
export async function getCampaignLog(id, offset = 0, limit = 200) {
  const resp = await apiFetch(
    `${HOST}/api/v1/campaigns/${id}/log?offset=${offset}&limit=${limit}`,
  );
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * List available trigger types with config schemas.
 * @returns {Promise<Array>}
 */
export async function getTriggerTypes() {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/trigger-types`);
  if (resp.status !== 200) throw await resp.text();
  const data = await resp.json();
  return data.items;
}

/**
 * Send a test push for a campaign to a customer by phone number.
 * @param {number} id — campaign ID
 * @param {string} phone — customer phone number
 * @returns {Promise<{title, body, customerName, delivered, attempted, errors, pruned}>}
 */
export async function testCampaign(id, phone) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone }),
  });
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Get campaign stats (candidates, sent, failed).
 * @param {number} id — campaign ID
 * @returns {Promise<{candidates: number|null, sent: number, failed: number}>}
 */
export async function getCampaignStats(id) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}/stats`);
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * The customers a campaign's holdout percentage excludes.
 * @param {number} id
 * @returns {Promise<{holdoutPct: number, rows: object[]}>}
 */
export async function getCampaignHoldout(id) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/${id}/holdout`);
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * List the segment names a campaign can target: the built-in snapshot
 * segments plus the operator-defined ones visible to it.
 * @param {number} [campaignId] — omit while creating a campaign
 * @returns {Promise<{names: string[], items: object[]}>}
 */
export async function getSegments(campaignId) {
  const query =
    campaignId == null ? "" : `?campaign_id=${encodeURIComponent(campaignId)}`;
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/segments${query}`);
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Validate a segment query and preview the customers it selects.
 * @param {string} query — a SELECT returning one `customer_id` column
 * @returns {Promise<{rows: object[], total: number|null}>}
 */
export async function previewSegmentQuery(query) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/segments/preview`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query }),
  });
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Create an operator-defined segment.
 * @param {object} data — { name, campaignId, query }
 * @returns {Promise<object>}
 */
export async function createSegment(data) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/segments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: data.name,
      campaignId: data.campaignId ?? null,
      query: data.query,
    }),
  });
  if (resp.status !== 201 && resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Rewrite the query behind an operator-defined segment.
 *
 * The name and the scope are immutable server-side: a campaign selects a
 * segment by name, so renaming one in place would detach every campaign
 * already pointing at it.
 * @param {number} id
 * @param {string} query — a SELECT returning one `customer_id` column
 * @returns {Promise<object>}
 */
export async function updateSegment(id, query) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/segments/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query }),
  });
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Delete an operator-defined segment.
 * @param {number} id
 */
export async function deleteSegment(id) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/segments/${id}`, {
    method: "DELETE",
  });
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}

/**
 * Get the analytics SQL for a campaign configuration.
 *
 * Takes the config from the form, so this works while creating a campaign
 * and while editing unsaved changes. `campaignId` is the saved campaign being
 * edited, if any: its sends and snapshot rows are what the query reads.
 * @param {string} triggerType
 * @param {object} config
 * @param {number | null | undefined} campaignId
 * @returns {Promise<{sql: string}>}
 */
export async function getCampaignSql(triggerType, config, campaignId) {
  const resp = await apiFetch(`${HOST}/api/v1/campaigns/sql`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      triggerType,
      config: config || {},
      campaignId: campaignId ?? null,
    }),
  });
  if (resp.status !== 200) throw await resp.text();
  return resp.json();
}
